/**
 * When the backend returns 200 from WorkOS logout but the HttpOnly session cookie
 * is not cleared, the next full page load still sees an authenticated cookie.
 * This guard tells auth initialization to ignore cookie-based SSO until the user
 * starts a new login or the cooling period expires.
 *
 * Call {@link armSessionIgnoreAfterLogout} synchronously at the very start of
 * logout (before any `await` or network) so a fast navigation cannot restore
 * cookie-based SSO while logout is still in flight.
 */
import { AUTH_STORAGE_KEYS } from "../constants/authStorageKeys";
import { logoutTrace } from "./logoutTrace";

/** @deprecated Use AUTH_STORAGE_KEYS.SESSION_IGNORE_COOKIE_UNTIL — kept for imports */
export const SESSION_IGNORE_COOKIE_UNTIL_KEY =
  AUTH_STORAGE_KEYS.SESSION_IGNORE_COOKIE_UNTIL;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

/**
 * Arms the guard so cookie-based SSO restoration is skipped until `ttlMs` elapses
 * or the user explicitly starts login (see {@link clearSessionIgnoreAfterLogout}).
 *
 * @param ttlMs - How long to ignore stale cookie sessions (default 10 minutes)
 */
export function armSessionIgnoreAfterLogout(ttlMs = 10 * 60 * 1000): void {
  if (!isBrowser()) return;
  try {
    const until = Date.now() + ttlMs;
    window.localStorage.setItem(
      SESSION_IGNORE_COOKIE_UNTIL_KEY,
      String(until)
    );
    logoutTrace("guard.arm", {
      key: SESSION_IGNORE_COOKIE_UNTIL_KEY,
      ttlMs,
      ignoreUntilIso: new Date(until).toISOString(),
    });
  } catch {
    // Storage may be unavailable in private mode or quota exceeded
    logoutTrace("guard.arm_failed", { reason: "localStorage" });
  }
}

/**
 * Clears the guard after the user intentionally signs in again.
 */
export function clearSessionIgnoreAfterLogout(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(SESSION_IGNORE_COOKIE_UNTIL_KEY);
  } catch {
    // ignore
  }
}

/**
 * @returns Whether cookie-based session restore should be skipped
 */
export function shouldIgnoreStaleCookieSession(): boolean {
  if (!isBrowser()) return false;
  try {
    const raw = window.localStorage.getItem(SESSION_IGNORE_COOKIE_UNTIL_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    if (Number.isNaN(until) || Date.now() >= until) {
      window.localStorage.removeItem(SESSION_IGNORE_COOKIE_UNTIL_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * OAuth authorization `code` from the current URL (query or hash).
 * Some flows put parameters in the fragment instead of `search`.
 */
export function getOAuthAuthorizationCodeFromLocation(): string | null {
  if (!isBrowser()) return null;
  try {
    const fromSearch = new URLSearchParams(window.location.search).get("code");
    if (fromSearch) return fromSearch;
    const hash = window.location.hash;
    if (!hash || hash.length <= 1) return null;
    const withoutHash = hash.slice(1);
    const queryPart = withoutHash.includes("?")
      ? withoutHash.split("?").slice(1).join("?")
      : withoutHash;
    return new URLSearchParams(queryPart).get("code");
  } catch {
    return null;
  }
}
